import { ChangeDetectorRef, Component, ViewChild } from '@angular/core';
import { combineLatest, map, tap, Observable, distinctUntilChanged, first } from 'rxjs';
import { ICreateRevisionRequest, IRevision, IUpdateRevisionRequest } from '../models/revision';
import { FormArray, FormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { AppStateInterface } from 'src/app/stores/appState.interface';
import { Store, select } from '@ngrx/store';
import { ActivatedRoute } from '@angular/router';
import * as CompanyActions from "../../stores/global/companies/company-actions";
import * as RevisionActions from "../../stores/global/revisions/actions";
import * as RevisionStatusActions from "../../stores/global/revision-status/actions";
import { PermissionStorageService } from 'src/app/authentication/storage/permission-storage.service';
import { companySelector } from 'src/app/stores/global/companies/company-selectors';
import { RevisionState, RevisionStore } from 'src/app/stores/component/revision-store';
import { revisionStatusSelector } from 'src/app/stores/global/revision-status/selectors';
import { DeviceStore } from 'src/app/stores/component/device-store';
import { EmployeeStore } from 'src/app/stores/component/employee-store';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { currentEmployeeSelector } from 'src/app/stores/global/current-employee/current-employee-selector';
import { WearingPartStore } from 'src/app/stores/component/wearing-parts-store';
import { DevicePollution, DeviceWear, IService, IUpdateServiceRequest, IUpdateServiceStatusRequest, ServiceStatus } from 'src/app/devices/models/service';
import { TranslocoService } from '@jsverse/transloco';
import { FileRestrictions, UploadEvent } from '@progress/kendo-angular-upload';
import { AttachmentStore } from 'src/app/stores/component/attachment-store';
import { AttachmentService } from 'src/app/shared/services/attachment.service';
import { DeviceServiceStore } from 'src/app/stores/component/device-service-store';
import { DeviceServiceService } from 'src/app/devices/services/service.service';
import { RevisionsDetailDevicesComponent } from './revisions-detail-devices/revisions-detail-devices.component';
import { SelectEvent } from '@progress/kendo-angular-layout';
import { IGetRevisionStatusResponse } from '../models/revision-status';
import { debug, RxJsLoggingLevel } from 'src/app/shared/rxjs-operators/debug-operator';
import { AddDevicesComponent } from './revisions-detail-devices/add-devices/add-devices.component';
import { PageSize } from 'src/app/core/models/base';

@UntilDestroy()
@Component({
    selector: 'app-revisions-detail',
    templateUrl: './revisions-detail.component.html',
    styleUrl: './revisions-detail.component.scss',
    providers: [
        RevisionStore,
        DeviceStore,
        EmployeeStore,
        WearingPartStore,
        AttachmentStore,
        DeviceServiceStore
    ],
    standalone: false
})
export class RevisionsDetailComponent {
  @ViewChild(RevisionsDetailDevicesComponent) addDevicesToRevisionComponent!: AddDevicesComponent;

  vm$: Observable<any>;
  isNew = false;
  isAdmin = false;
  currentUserCompany: string;
  tenantOnRevision: string;
  revisionId: string;
  revisionReportDocumentId: string;
  revisionStatus: IGetRevisionStatusResponse;
  companyId: string;
  private originalFormValues: any;

  // Initialize form
  public revisionForm: UntypedFormGroup;

  constructor(
    private store: Store<AppStateInterface>,
    private route: ActivatedRoute,
    private cdr: ChangeDetectorRef,
    private fb: FormBuilder,
    private revisionStore: RevisionStore,
    private deviceStore: DeviceStore,
    private employeeStore: EmployeeStore,
    private permissionServie: PermissionStorageService,
    private wearingPartStore: WearingPartStore,
    private translocoService: TranslocoService,
    private attachmentService: AttachmentService,
  ) { 
    this.vm$ = combineLatest([ 
      this.employeeStore.availableHtecEmployeesForRevision$,
      this.employeeStore.htecEmployees$.pipe(
        map(employees =>
          employees.map(employee => ({
            ...employee,
            fullName: `${employee.firstName} ${employee.lastName}`
          }))
        )
      ),
      this.store.pipe(select(companySelector)),
      this.store.pipe(select(revisionStatusSelector)).pipe(
        tap(status => {
          if (this.revisionId == "new") {
            this.revisionForm.patchValue({
              revisionStatusId: status[0]?.id
            });
          }
        })
      ),
      this.revisionStore.selectedRevision$.pipe(
        tap(revision => {
          if (revision) {
            this.revisionId = revision.id;
            this.companyId = revision.company?.id;
            this.revisionStatus = revision.revisionStatus;
            this.revisionReportDocumentId = revision.revisionReportDocumentId;

            this.employeeStore.getHtecEmployeesForRevision({
              engineers: revision.engineers,
            });

            this.tenantOnRevision = revision.company.tenantId;

            this.patchFormValues(revision);
          }
      })),
      this.store.pipe(select(currentEmployeeSelector)).pipe(
        tap(user => {
          // Ensure user is defined
          if (user) {
            const companyId = user.company?.id;
    
            // Only call getEmployeesByCompany if user is an admin and companyId is valid
            if (this.isAdmin && companyId) {
              this.employeeStore.getEmployeesByCompany(companyId);
            }
          }
        })
      )
    ]).pipe(
      map(([
        availableHtecEmployeesForRevision,
        htecEmployees,
        companies,
        revisionStatus,
        selectedRevision,
        currentEmployee
      ]) => ({
        availableHtecEmployeesForRevision,
        htecEmployees,
        companies,
        revisionStatus,
        selectedRevision,
        currentEmployee
      }))
    );

    // Initialize forms
    this.revisionForm = this.fb.group({
      scheduledDate: new UntypedFormControl(null, {
        validators: Validators.required,
        updateOn: 'change'
      }),
      revisionStatusId: new UntypedFormControl(null, {
        validators: Validators.required,
        updateOn: 'change'
      }),
      revisionStatusIsFinal: new UntypedFormControl(null),
      reporterId: new UntypedFormControl(null),
      orderNumber: new UntypedFormControl(''),
      orderNumberCustomer: new UntypedFormControl(''),
      remarks: new UntypedFormControl(''),
      companyId: new UntypedFormControl(null, {
        validators: Validators.required,
        updateOn: 'change'
      }),
      companyContact: new UntypedFormControl(''),
      specifications: new UntypedFormControl(''),
      process: new UntypedFormControl(''),
      recommendations: new UntypedFormControl(''),
      engineers: this.fb.array([])
    });
  }

  revisionRestrictions: FileRestrictions = {
    allowedExtensions: [".pdf"],
  };

  ngOnInit(): void {
    this.checkPermissions();

    this.route.paramMap.subscribe(params => {
      this.revisionId = params.get('id');
    });

    if (this.revisionId == "new") {
      this.isNew = true;
    } else {
      this.isNew = false;
      this.revisionStore.getRevisionById(this.revisionId);
    }

    if (this.isAdmin) {
      this.store.dispatch(CompanyActions.getCompanies());
      this.store.dispatch(RevisionStatusActions.getRevisionStatus());
    }
  }

  checkPermissions(): void {
    const permissions = this.permissionServie.getPermissions();
    
    if (permissions && !permissions.includes('Permissions.Revisions.SearchGlobal')) {
      this.revisionForm.disable();
      this.isAdmin = false;
    } else {
      this.isAdmin = true;
    }
  }

  private patchFormValues(revision: IRevision): void {
    this.populateRevisionForm(revision);
  }

  populateRevisionForm(revision: IRevision) {
    this.revisionForm.patchValue({
      scheduledDate: revision.scheduledDate ? new Date(revision.scheduledDate) : null,
      revisionStatusId: revision.revisionStatus.id,
      revisionStatusIsFinal: revision.revisionStatus.isFinalStatus,
      orderNumber: revision.orderNumber,
      orderNumberCustomer: revision.orderNumberCustomer,
      remarks: revision.remarks,
      companyId: revision.company.id,
      reporterId: revision.reporter?.id,
      companyContact: revision.companyContact,
      specifications: revision.specifications,
      process: revision.process,
      recommendations: revision.recommendations
    });

    // Clear existing engineers FormArray
    this.engineers.clear();

    // Populate engineers
    revision?.engineers?.forEach(engineer => {
      this.engineers.push(this.fb.group({
          id: engineer.id,
          firstName: engineer.firstName,
          lastName: engineer.lastName
      }));
    });
  }

  onCompanyChange(companyId: string | null): void {
    if (!this.addDevicesToRevisionComponent) return;

    if (companyId) {
      // Reset and update the child form
      this.addDevicesToRevisionComponent.resetDevices();
      this.addDevicesToRevisionComponent.enableDevices();
      // Simulate fetching devices for the selected company
    } else {
      this.addDevicesToRevisionComponent.disableDevices();
    }
  }

  /*
  onCompanyChange(event: Event) {
    // reset devices
    this.deviceForm.get('devices')?.reset();

    // get devices based on chosen company
    if (event !== null) {
      var id = String(event);
      this.deviceStore.getDevicesByCompanyForRevision({companyId: id, linkedDevices: null });
      this.deviceForm.get("devices").enable();
    } else {
      this.deviceForm.get("devices").disable();
    }

    // Example triggering change detection manually
    this.cdr.detectChanges();
  }
    */

  saveChanges() {
    const formValues = this.revisionForm.value;

    // Extract engineer IDs from the form
    const linkedEngineerIds = this.engineers.controls.map((engineerGroup) => engineerGroup.get('id')?.value);

    var selectedDate = new Date(formValues.scheduledDate);
    let utcDate = new Date(Date.UTC(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate()));
    let formattedDate = utcDate.toISOString();

    if (!this.isNew) {
      const updateRequest: IUpdateRevisionRequest = {
        id: this.revisionId,
        scheduledDate: formattedDate,
        revisionStatusId: formValues.revisionStatusId,
        reporterId: formValues.reporterId,
        orderNumber: formValues.orderNumber,
        orderNumberCustomer: formValues.orderNumberCustomer,
        remarks: formValues.remarks,
        companyContact: formValues.companyContact,
        specifications: formValues.specifications,
        process: formValues.process,
        recommendations: formValues.recommendations,
        engineerIds: linkedEngineerIds
      };

      this.store.dispatch(RevisionActions.updateRevision({ revision: updateRequest }));
    } else {
      const createRequest: ICreateRevisionRequest = {
        scheduledDate: formattedDate,
        revisionStatusId: formValues.revisionStatusId,
        reporterId: formValues.reporterId,
        orderNumber: formValues.orderNumber,
        orderNumberCustomer: formValues.orderNumberCustomer,
        remarks: formValues.remarks,
        companyId: formValues.companyId,
        companyContact: formValues.companyContact,
        specifications: formValues.specifications,
        process: formValues.process,
        recommendations: formValues.recommendations,
        engineerIds: linkedEngineerIds
      };

      this.store.dispatch(RevisionActions.createRevision({ revision: createRequest }));
    }
  }

  // disable finishing revision status from dropdown
  public itemDisabled(itemArgs: { dataItem: { isFinalStatus: boolean } }): boolean {
    return itemArgs.dataItem.isFinalStatus === true;
  }

  delete() {
    if(!this.isNew) {
      this.store.dispatch(RevisionActions.deleteRevision({ id: this.revisionId }))
    }
  }

  get engineers(): FormArray {
    return this.revisionForm.get('engineers') as FormArray;
  }

  addEngineer(id, firstName, lastName): void {
    // Create a new FormGroup with engineer details
    const engineerFormGroup = this.fb.group({
      id: [id],
      firstName: [firstName],
      lastName: [lastName]
    });

    // Push the new FormGroup into the FormArray
    this.engineers.push(engineerFormGroup);
  }

  linkEngineer(id, firstName, lastName) {
    this.addEngineer(id, firstName, lastName);
    this.employeeStore.removeEmployeeByIdForRevision(id);
    this.revisionForm.markAsDirty();
  }

  removeEngineer(id: string, index: number): void {
    this.engineers.removeAt(index);
    this.employeeStore.addEmployeeByIdForRevision(id);
    this.revisionForm.markAsDirty();
  }

  // tab element
  public onTabSelect(e: SelectEvent): void {
  }

  // delete confirmation dialog
  public deletionDialogOpened = false;

  public deletionDialogClose(status: string): void {
    this.deletionDialogOpened = false;

    if(status == 'delete') {
      this.delete();
    }
  }

  public deletionDialogOpen(): void {
    this.deletionDialogOpened = true;
  }

  // complete revision confirmation dialog
  public completeRevisionDialogOpened = false;

  public completeRevisionDialogClose(status: string): void {
    this.completeRevisionDialogOpened = false;

    if(status == 'complete') {
      this.store.dispatch(RevisionActions.completeRevision({id: this.revisionId}));
    }
  }

  public completeRevisionDialogOpen(): void {
    this.completeRevisionDialogOpened = true;
  }  
}